// ─────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH FOR ALL PRICING.
// Values below are defaults; on boot they are replaced live from
// GET /api/pricing (admin-editable). Never hardcode a price elsewhere —
// read from PRICING / usePricing() instead.
// Displayed prices are FINAL, GST-INCLUSIVE amounts (₹).
// ─────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import axios from "axios";

export const GST_RATE = 0.18;
export const PRICES_INCLUDE_GST = true;

export const PRICING = {
  coursePrice: 1499,
  totalCourses: 10,
  maxCartCourses: 10,
  premiumActualValue: 36984,
  premiumPerks: [
    { label: "Personal Mentorship Access", value: 4999 },
    { label: "1% Community Club Access", value: 8999 },
    { label: "Priority Support", value: 1999 },
    { label: "Live Growth Sessions", value: 2999 },
    { label: "WhatsApp Community Access", value: 999 },
    { label: "Premium Learning Resources", value: 1999 },
  ],
  tiers: {
    elite: { id: "elite", name: "E-Lite Package", price: 2999, strike: 4999, courseCount: 2, pickCount: 2, freeCount: 0 },
    basic: { id: "basic", name: "Basic Package", price: 4999, strike: 8999, courseCount: 5, pickCount: 3, freeCount: 2 },
    premium: { id: "premium", name: "Premium Package", price: 7999, strike: 14999, courseCount: 10, pickCount: 10, freeCount: 0 },
  },
};

export const PACKAGE_ORDER = ["elite", "basic", "premium"];

const listeners = new Set();

/** Live pricing — re-renders the component whenever the config is (re)loaded. */
export function usePricing() {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((v) => v + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return PRICING;
}

export async function hydratePricing() {
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`);
    if (!data?.tiers) return;
    PRICING.coursePrice = Math.round(data.course_price_paise / 100);
    PRICING.totalCourses = data.total_courses || PRICING.totalCourses;
    PRICING.premiumActualValue = Math.round(data.premium_actual_value_paise / 100);
    if (Array.isArray(data.premium_perks)) {
      PRICING.premiumPerks = data.premium_perks.map((p) => ({
        label: p.label,
        value: Math.round(p.value_paise / 100),
      }));
    }
    let maxCount = 0;
    Object.entries(data.tiers).forEach(([id, t]) => {
      const prev = PRICING.tiers[id] || {};
      PRICING.tiers[id] = {
        ...prev,
        id,
        name: t.name,
        price: Math.round(t.price_paise / 100),
        strike: t.strike_paise ? Math.round(t.strike_paise / 100) : null,
        courseCount: t.course_count,
        pickCount: prev.pickCount ?? t.course_count,
        freeCount: prev.freeCount ?? 0,
        active: t.active !== false,
      };
      if (t.active !== false) maxCount = Math.max(maxCount, t.course_count || 0);
    });
    PRICING.maxCartCourses = maxCount || PRICING.maxCartCourses;
    listeners.forEach((l) => l());
  } catch {
    // keep defaults — the site stays fully usable offline of the pricing API
  }
}

/** Which package a cart of `n` courses unlocks (null = no package tier). */
export function tierForCourseCount(n) {
  return (
    PACKAGE_ORDER.map((k) => PRICING.tiers[k]).find(
      (t) => t && t.active !== false && t.courseCount === n,
    ) || null
  );
}

/** Split a GST-inclusive total into taxable value + GST. */
export function extractGst(total) {
  const taxable = Math.round((total || 0) / (1 + GST_RATE));
  return { taxable, gst: (total || 0) - taxable };
}

export function premiumValueItems(courseTitles = []) {
  return [
    ...courseTitles.map((t) => ({ label: t, value: PRICING.coursePrice })),
    ...PRICING.premiumPerks,
  ];
}
